import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Cookie, X } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { COOKIE_CONSENT_OPEN_EVENT, getCookieConsent, setCookieConsent } from "@/lib/consent";

const TEXTS = {
  fr: {
    title: "Confidentialité",
    message:
      "Aevo utilise des cookies nécessaires au fonctionnement du site. Avec votre accord, nous mesurons l'usage et les erreurs afin d'améliorer l'expérience.",
    reassurance: "Aucun cookie publicitaire. Aucune revente de données.",
    accept: "Accepter",
    decline: "Refuser",
    learnMore: "En savoir plus sur la confidentialite",
  },
  en: {
    title: "Privacy",
    message:
      "Aevo uses cookies required for the site to work. With your consent, we measure usage and errors to improve the experience.",
    reassurance: "No advertising cookies. No data resale.",
    accept: "Accept",
    decline: "Decline",
    learnMore: "Learn more about privacy",
  },
  es: {
    title: "Privacidad",
    message:
      "Aevo usa cookies necesarias para que el sitio funcione. Con tu permiso, medimos el uso y los errores para mejorar la experiencia.",
    reassurance: "Sin cookies publicitarias. Sin reventa de datos.",
    accept: "Aceptar",
    decline: "Rechazar",
    learnMore: "Mas sobre privacidad",
  },
  ar: {
    title: "الخصوصية",
    message: "تستخدم Aevo ملفات تعريف ارتباط ضرورية لعمل الموقع. وبموافقتك، نقيس الاستخدام والأخطاء لتحسين التجربة.",
    reassurance: "لا توجد ملفات إعلانية ولا نبيع البيانات.",
    accept: "قبول",
    decline: "رفض",
    learnMore: "تفاصيل سياسة الخصوصية",
  },
};

export default function CookieBanner() {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);
  const copy = TEXTS[lang] || TEXTS.fr;
  const isRTL = lang === "ar";

  useEffect(() => {
    if (!getCookieConsent()) {
      const timer = window.setTimeout(() => setVisible(true), 5000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, open);
    return () => window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, open);
  }, []);

  const accept = () => {
    setCookieConsent("accepted");
    setVisible(false);
  };

  const decline = () => {
    setCookieConsent("declined");
    setVisible(false);
    Promise.all([import("@/lib/analytics"), import("@/lib/monitoring")]).then(([analytics, monitoring]) => {
      analytics.disableAnalytics();
      monitoring.disableMonitoring();
    });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-sm md:left-auto md:right-6"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div className="rounded-2xl border border-border bg-card p-4 shadow-elevated">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <Cookie className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-foreground">{copy.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-foreground">{copy.message}</p>
                <p className="mt-1 text-xs font-semibold text-muted-foreground">{copy.reassurance}</p>
                <a href="/privacy" className="mt-1 inline-flex min-h-11 items-center text-xs font-semibold text-primary hover:underline">
                  {copy.learnMore}
                </a>
              </div>
              <button
                type="button"
                onClick={decline}
                aria-label={copy.decline}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={decline}
                className="h-11 flex-1 rounded-xl border border-border text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                {copy.decline}
              </button>
              <button
                type="button"
                onClick={accept}
                className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary text-sm font-semibold text-primary-foreground transition-all hover:brightness-110"
              >
                <Check className="h-3.5 w-3.5" />
                {copy.accept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
